import { createTheme } from "@mui/material/styles";
import { DisplayMode } from "@/types";

// Shared component overrides for both light & dark themes
const sharedComponents = {
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
      size: "medium" as const,
      variant: "filled" as const,
    },
  },
  MuiCssBaseline: {
    styleOverrides: `
      [class*="RaTabbedShowLayout"] .MuiDivider-root {
        display: block !important;
      }
    `,
  },
};

export const theme = createTheme({
  typography: {
    fontFamily: "Montserrat, sans-serif",
    fontSize: 16,
    h1: { fontSize: "32px", fontWeight: 700, lineHeight: "1.25" },
    h2: { fontSize: "24px", fontWeight: 600, lineHeight: "1.3" },
    h3: { fontSize: "20px", fontWeight: 600, lineHeight: "1.4" },
    h4: { fontSize: "18px", fontWeight: 600, lineHeight: "1.4" },
    h5: { fontSize: "16px", fontWeight: 600, lineHeight: "1.4" },
    h6: { fontSize: "14px", fontWeight: 600, lineHeight: "1.4" },
    body1: { fontSize: "16px", fontWeight: 400, lineHeight: "1.5" },
    body2: { fontSize: "14px", fontWeight: 400, lineHeight: "1.4" },
    button: {
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "1.4",
      textTransform: "none",
    },
    caption: { fontSize: "12px", fontWeight: 400, lineHeight: "1.4" },
    overline: {
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "1.3",
      textTransform: "uppercase",
    },
  },
  palette: {
    mode: DisplayMode.LIGHT,
    primary: {
      main: "#2A7D99",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#192E58",
    },
    background: {
      default: "#F5F5F5",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#6E6E6E",
    },
  },
  spacing: 8,
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorSecondary: {
          backgroundColor: "#368CAA",
          color: "#fff",
        },
      },
    },
    ...sharedComponents,
  },
});

export const darkTheme = createTheme({
  typography: theme.typography,
  palette: {
    mode: DisplayMode.DARK,
    primary: {
      main: "#DF8E01",
      contrastText: "#000000",
    },
    secondary: {
      main: "#FEA30B",
      contrastText: "#000000",
    },
    warning: {
      main: "#FBBF24",
      contrastText: "#000000",
    },
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#BCC0CC",
    },
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          color: "#000000",
        },
      },
    },
    ...sharedComponents,
  },
});

// ─── Partner-branded theme factories ─────────────────────────────────

export interface PartnerBranding {
  brand_primary: string;
  brand_secondary: string;
  brand_primary_dark: string;
  brand_secondary_dark: string;
}

/**
 * Creates a light theme with partner brand colors layered over the base Aegle theme.
 * Only overrides palette values for colors that are actually set (non-empty, valid hex).
 */
export const createPartnerTheme = (branding: PartnerBranding) =>
  createTheme(theme, {
    palette: {
      ...(branding.brand_primary && {
        primary: {
          main: branding.brand_primary,
          contrastText: "#FFFFFF",
        },
      }),
      ...(branding.brand_secondary && {
        secondary: {
          main: branding.brand_secondary,
        },
      }),
    },
    components: {
      ...(branding.brand_primary && {
        MuiAppBar: {
          styleOverrides: {
            colorSecondary: {
              backgroundColor: branding.brand_primary,
              color: "#fff",
            },
          },
        },
      }),
    },
  });

/**
 * Creates a dark theme with partner brand colors layered over the base Aegle dark theme.
 * Only overrides palette values for colors that are actually set (non-empty, valid hex).
 */
export const createPartnerDarkTheme = (branding: PartnerBranding) =>
  createTheme(darkTheme, {
    palette: {
      ...(branding.brand_primary_dark && {
        primary: {
          main: branding.brand_primary_dark,
          contrastText: "#000000",
        },
      }),
      ...(branding.brand_secondary_dark && {
        secondary: {
          main: branding.brand_secondary_dark,
          contrastText: "#000000",
        },
      }),
    },
  });
